import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { isCreatingInLms } from '@/features/document-registration/lms-sync-presentation';
import { useApi } from '@/lib/api-context';

import { dossierResponseSchema, type Dossier } from './types';

interface Options {
  /** Empresa activa: entra al queryKey para no servir el expediente cacheado de otra empresa. */
  companyId: string | null;
  /** No dispara hasta conocer la empresa activa (garantiza X-Active-Company en carga fría/deep-link). */
  enabled: boolean;
  /** Cada cuánto se refresca mientras el LMS crea el documento (pruebas: más corto). */
  pollMs?: number;
}

/** Refresco mientras el LMS crea el documento: el envío sale en segundos. */
const CREATION_POLL_MS = 2_000;

/** Expediente completo de un documento. Valida el contrato con Zod: un drift del
 *  backend cae al estado de error de la query (nunca pantalla blanca). Un 404 llega
 *  como ApiError con status 404, que la pantalla distingue del error genérico. */
export function useDossier(
  id: string,
  { companyId, enabled, pollMs = CREATION_POLL_MS }: Options,
): UseQueryResult<Dossier> {
  const api = useApi();
  return useQuery({
    queryKey: ['dossier', companyId, id],
    queryFn: async () => {
      const raw = await api.get<unknown>(`/api/v1/quality/master-list/${id}/dossier`);
      return dossierResponseSchema.parse(raw).data;
    },
    enabled,
    // Recorrido 2026-11-04: mientras el LMS crea el documento desde la plantilla, la ficha
    // se refresca sola hasta que el vínculo quede confirmado.
    refetchInterval: (query) => (isCreatingInLms(query.state.data?.lmsSync) ? pollMs : false),
  });
}
